import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { lastValueFrom, map } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../environments/environment';
import { gTDB } from './shared/services/db';
import { DeviceInfoService } from './shared/services/device-info.service';
import { SqliteDbService } from './shared/services/sqlite-db.service';
import { NativeBgUploadService } from './shared/services/native-bg-upload.service';
import { SyncClockService } from './shared/services/sync-clock.service';
declare var bootstrap: any;

@Injectable({
  providedIn: 'root',
})
export class AppService {
  isNativeApp = Capacitor.isNativePlatform();

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private route: Router,
    private deviceInfoService: DeviceInfoService,
    private injector: Injector,
    private clockService: SyncClockService,
    private sqliteDb: SqliteDbService,
    private nativeUploadService: NativeBgUploadService,
  ) {
    // This will skip auth interceptor. Need to learn which apis can be moved. This would have been done because earlier we had lot of codes that didn't have auth tokens
    this.http = new HttpClient(httpBackend);
  }
  switchGTAppTheme(theme: string = 'light', store = true): void {
    if (!['default', 'dark'].includes(theme)) {
      theme = 'default';
    }
    if (store) this.setGTAppTheme(theme);
    theme = theme === 'dark' ? theme : 'light';
    document.documentElement.setAttribute('data-bs-theme', theme);
  }
  getGTAppTheme() {
    return localStorage.getItem('gtAppTheme') || 'default';
  }
  setGTAppTheme(theme: string = 'light') {
    localStorage.setItem('gtAppTheme', theme);
  }

  getUserData() {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  }

  setUserData(data: any) {
    this.sendToSW(data);
    localStorage.setItem('userData', JSON.stringify(data));

    localStorage.setItem(
      btoa('INSTANCE_REGION_NAME'),
      data?.INSTANCE_REGION_NAME || 'AUSTRALIA',
    );
  }
  sendToSW(data: any) {
    let payload: any = { type: 'userData' };
    Object.keys(data).forEach((key) => {
      if (['access', 'profile', 'subscriber'].includes(key)) {
        payload[key] = data[key];
      }
    });
    navigator?.serviceWorker?.controller?.postMessage(payload);
  }
  getToken() {
    return this.getUserData()?.access;
  }
  getModifiedFullApiPath(partialPath: string): string {
    if (
      this.isNativeApp &&
      'nativeAppUrl' in environment &&
      environment.nativeAppUrl
    ) {
      const reqUrl = partialPath?.startsWith('/')
        ? partialPath
        : `/${partialPath}`;
      return `https://${environment.nativeAppUrl}${reqUrl}`;
    }
    return partialPath;
  }

  cityLookup(input: any) {
    const body = { search_str: input };
    return this.http.post(
      this.getModifiedFullApiPath('api/shared/suburb_lookup/'),
      body,
    );
  }
  getCountries() {
    return this.http.get(this.getModifiedFullApiPath('api/shared/country/'));
  }
  checkServerStatus() {
    return this.http.get(this.getModifiedFullApiPath('api/shared/health/'));
  }
  getStates(countryId: any, params?: any) {
    return this.http.get(
      this.getModifiedFullApiPath('api/shared/state/' + countryId + '/'),
      {
        params: params,
      },
    );
  }
  addressLookupShared(input: any, params?: any) {
    return this.http.post(
      this.getModifiedFullApiPath('api/shared/address/'),
      { search_str: input },
      { params: params },
    );
  }

  logOut() {
    let user = this.getUserData();
    let email = user?.profile?.email;
    let expiryToken = user?.token_expiry;
    const gtAppTheme: any = this.getGTAppTheme();
    const uniqueDeviceID = localStorage.getItem(btoa('uniqueDeviceID'));
    const isIOSFirstTimeUser =
      localStorage.getItem(btoa('isIOSFirstTimeUser')) || 'false';
    this.route.navigate(['/login']).then(() => {
      // first timeout is so that the localStorage will only be cleared
      // after the page routes to login, other wise the user will see an Invalid Subscription message
      setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        setTimeout(() => {
          // second timeout to set the theme and email to the localStorage to
          // change the login page and form, without it the async nature may cause it to clear the localStorage
          if (expiryToken) {
            localStorage.setItem('email', email);
          }
          localStorage.setItem(btoa('isIOSFirstTimeUser'), isIOSFirstTimeUser);
          if (uniqueDeviceID)
            localStorage.setItem(btoa('uniqueDeviceID'), uniqueDeviceID);
          this.setGTAppTheme(gtAppTheme);
          window.location.reload();
        }, 10);
      }, 300);
    });
  }
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  async getDisplayUrlForFileInDevice(relativePath: string): Promise<string> {
    const { uri } = await Filesystem.getUri({
      path: relativePath,
      directory: Directory.Data,
    });
    return Capacitor.convertFileSrc(uri);
  }

  async formDataApi(
    path: string,
    formData: any,
    admin: true | false = false,
    params?: any,
    method: 'POST' | 'PUT' = 'POST',
    addDeviceInfo: true | false = true,
    gps?: any,
  ) {
    // Retrieve authorization token and API extras.
    const auth_token = this.getToken();
    const apiResponse: any =
      await this.deviceInfoService.getApiHeadersAndExtras(auth_token);

    const headerDict = apiResponse?.headerDict;
    const reqBody = apiResponse?.reqBody;

    if (addDeviceInfo) {
      let fileGps: any = {};
      let device_info: any;

      if (formData.get('exif_data')) {
        try {
          const exifData = JSON.parse(formData.get('exif_data') as string);

          if (exifData?.gps?.lat != null && exifData?.gps?.lon != null) {
            fileGps = exifData.gps;
          }
        } catch (error) {
          throw error;
        }

        const [deviceInfo, ip]: any = await Promise.all([
          this.deviceInfoService.getDeviceInfo(),
          this.deviceInfoService.getIp(),
        ]);

        device_info = {
          ...deviceInfo,
          gps: { ...fileGps, ip },
          exif_data: true,
        };
      } else {
        device_info = await this.deviceInfoService.getDeviceDetail('must', gps);
      }

      Object.keys(reqBody).forEach((key) => {
        if (key !== 'device_info') {
          formData.append(key, reqBody[key]);
        }
      });

      formData.set('device_info', JSON.stringify(device_info));
    }

    formData.set(
      'updated_at',
      formData.get('updated_at') || this.clockService.nowMs(),
    );
    const updatedPath = this.getModifiedFullApiPath(
      `${admin ? 'admin' : 'api'}/${path}/`,
    );

    return await lastValueFrom(
      this.http.request(method, updatedPath, {
        body: formData,
        headers: headerDict,
        params: params,
      }),
    );
  }
  async backUpAndUploadUpdate(
    path: string,
    formData: any,
    params?: any,
    method: 'POST' | 'PUT' = 'POST',
    gps?: any,
  ) {
    // Retrieve authorization token and API extras.
    const auth_token = this.getToken();
    const apiResponse: any =
      await this.deviceInfoService.getApiHeadersAndExtras(auth_token);

    const headerDict = apiResponse?.headerDict;
    const reqBody = apiResponse?.reqBody;

    let fileGps: any = {};
    let device_info: any;

    if (formData.get('exif_data')) {
      try {
        const exifData = JSON.parse(formData.get('exif_data') as string);

        if (exifData?.gps?.lat != null && exifData?.gps?.lon != null) {
          fileGps = exifData.gps;
        }
      } catch (error) {
        throw error;
      }

      const [deviceInfo, ip]: any = await Promise.all([
        this.deviceInfoService.getDeviceInfo(),
        this.deviceInfoService.getIp(),
      ]);

      device_info = {
        ...deviceInfo,
        gps: { ...fileGps, ip },
        exif_data: true,
      };
    } else {
      device_info = await this.deviceInfoService.getDeviceDetail('must', gps);
    }

    Object.keys(reqBody).forEach((key) => {
      if (key !== 'device_info') {
        formData.append(key, reqBody[key]);
      }
    });

    formData.set('device_info', JSON.stringify(device_info));
    formData.set(
      'updated_at',
      formData.get('updated_at') || this.clockService.nowMs(),
    );

    const updatedPath = this.getModifiedFullApiPath(`api/${path}/`);
    if (this.isNativeApp) {
      let uploadedFileBase64: any;
      let file: any;
      const uploadBodyDict: any = {};
      for (const [key, value] of formData.entries()) {
        if (key == 'file' && value) {
          file = value;
        } else if (key == 'uploadedFileBase64' && value) {
          // for some cases we are already converting it (image preview cases). so no need to do it here also
          uploadedFileBase64 = value;
        } else {
          uploadBodyDict[key] = value;
        }
      }
      let relativePath: string = '';
      let eventId: string = '';
      let jobType: string = '';

      switch (path) {
        case 'job/update_job':
          eventId = formData.get('job_id');
          jobType = 'job';
          break;

        case 'patrol/scan_checkpoint':
          eventId =
            formData.get('job_id') ||
            formData.get('patrol_route_id') ||
            formData.get('check_point_id');
          jobType = 'scan_checkpoint';
          break;
        case 'patrol/add_update':
          eventId = formData.get('patrol_route_id') || formData.get('site_id');
          jobType = 'patrol';
          break;
        case 'check_point/register_qr':
          const qrFormData = JSON.parse(formData.get('form_data')) || {};
          eventId = qrFormData?.site_id || uuidv4();
          jobType = 'register_qr';

          break;
        case 'run_sheet/add_update':
          eventId = formData.get('run_sheet_id');
          jobType = 'runsheet';
          break;
      }

      if (file) {
        uploadBodyDict['mimeType'] = file.type;
        if (!uploadedFileBase64) {
          uploadedFileBase64 = await this.fileToBase64(file);
        }
        relativePath = `GuardTracker/file-uploads/${eventId}/${uuidv4()}_${file.name}`;
        await Filesystem.writeFile({
          path: relativePath,
          data: uploadedFileBase64,
          directory: Directory.Data,
          recursive: true,
        });
      }
      try {
        this.sqliteDb.insertSavedUpdate(jobType, eventId, {
          body: uploadBodyDict,
          params: params,
          uploadedFilePath: relativePath,
        });
      } catch (error) {
        throw error;
      }
      this.nativeUploadService.syncIfOnline();

      return { status: 'success', message: 'Update Queued' };
    }

    return await lastValueFrom(
      this.http.request(method, updatedPath, {
        body: formData,
        headers: headerDict,
        params: params,
      }),
    );
  }
  addFeedBack(requestData: any) {
    let body = {
      ...requestData,
    };
    return this.http
      .post(
        this.getModifiedFullApiPath(`api/external_api/save_feedback/`),
        body,
      )
      .pipe(map((response: any) => response));
  }
  postErrorToBackend(requestData: any) {
    return this.http
      .post(
        this.getModifiedFullApiPath(`api/external_api/ui_error/`),
        requestData,
      )
      .pipe(map((response: any) => response));
  }

  async gtExternalLink(url: any, body: any, token: any, addGps = true) {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('token', token);
    headers = headers.append('Content-type', 'application/json');
    headers = headers.append('Accept', 'application/json');
    let requestBody: any = body;
    if (addGps) {
      requestBody.device_info = await this.deviceInfoService.getDeviceDetail();
    }
    requestBody.isPwa = Boolean(
      window.matchMedia('(display-mode: standalone)').matches,
    );

    return await lastValueFrom(
      this.http.post(this.getModifiedFullApiPath(`api/${url}/`), requestBody, {
        headers,
      }),
    );
  }

  getReverseLocation(lat: any, lon: any) {
    // use this with caution ; this api may be deprecated or most probably restricted.
    let params = {
      location: `${lon}, ${lat}`,
      f: 'pjson',
      distance: 50000,
      outSR: '',
    };
    return this.http
      .get(
        'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode',
        { params: params },
      )
      .pipe(map((response: any) => response));
  }
  getSWUpdate() {
    try {
      return this.injector.get(SwUpdate, null);
    } catch {
      return null;
    }
  }
  async updateGuardTrackerApp(falseUpdate = false) {
    const swUpdate: any = this.getSWUpdate();

    if (swUpdate?.isEnabled) {
      try {
        if (await swUpdate?.checkForUpdate()) {
          const updateApp = await swUpdate?.activateUpdate();
          sessionStorage.setItem('updateAvailable', 'true');
          this.deviceInfoService.deviceInfoSubject.next({
            appNewVersionAvailable: true,
          });
        } else if (falseUpdate) {
          sessionStorage.setItem('updateAvailable', 'true');
          this.deviceInfoService.deviceInfoSubject.next({
            appNewVersionAvailable: true,
          });
        }
      } catch (error) {
        throw error;
      }
    }
  }

  async getVersionInfo() {
    // api called in login to check whether the Ui has been updated or not
    return lastValueFrom(
      this.http.get(
        this.getModifiedFullApiPath('api/shared/get_version_info/'),
      ),
    );
  }
  async getDeviceMinVersionCheck(params: any) {
    return lastValueFrom(
      this.http.get(
        this.getModifiedFullApiPath('api/shared/get_device_min_version_check/'),
        {
          params: params,
        },
      ),
    );
  }
  async updateAppVersionIndexedDb(version: string) {
    await gTDB.userData.delete('appVersion');
    await gTDB.userData.add({ version: version }, 'appVersion');
  }
  async appVersionCheck(latestVersion?: any) {
    const swUpdate: any = this.getSWUpdate();

    return new Promise(async (resolve) => {
      try {
        if (!swUpdate?.isEnabled) {
          resolve(null);
        }

        if (!latestVersion?.last_item?.version) {
          const response: any = await this.getVersionInfo().catch(() =>
            resolve(null),
          );
          if (response?.status === 'success') {
            latestVersion = response;
          }
        }

        const currentVersion: any = await this.getCurrentVersionInfo();

        if (
          currentVersion?.version &&
          latestVersion?.last_item?.version &&
          latestVersion?.last_item?.version !== currentVersion?.version
        ) {
          const updateAvailabeToReload =
            sessionStorage.getItem('updateAvailable') === 'true';

          // if there is an update already to reload the cache don't do extra checks
          // check if the appversion in index db is different from the api call version implying an update is there
          const lastestVersion = localStorage.getItem(btoa('latestAppVerson'));
          if (
            lastestVersion &&
            lastestVersion === latestVersion?.last_item?.version &&
            !updateAvailabeToReload
          ) {
            // storing th latest version in localstorage and will be updated in the index db only after the cache reload takes place
            localStorage.removeItem(btoa('latestAppVerson'));
            this.updateAppVersionIndexedDb(latestVersion?.last_item?.version);
          } else {
            // if value is not there in the localstorage, need to add it there and force the app to do a reload so that cache will be cleared.
            localStorage.setItem(
              btoa('latestAppVerson'),
              latestVersion?.last_item?.version,
            );
            this.updateGuardTrackerApp(true);
          }
        }
        if (!currentVersion?.version) {
          this.updateAppVersionIndexedDb(latestVersion?.last_item?.version);
        }

        resolve(currentVersion);
      } catch (error) {
        resolve(null);
      }
    });
  }
  async getCurrentVersionInfo() {
    return new Promise(async (resolve) => {
      try {
        setTimeout(async () => {
          const data = await gTDB.userData.get('appVersion');

          resolve(data);
        }, 500);
      } catch (error) {
        resolve([]);
      }
    });
  }

  initializeBootstrapTooltips() {
    setTimeout(() => {
      var tooltipTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="tooltip"]'),
      );

      var tooltipList = tooltipTriggerList?.map(function (tooltipTriggerEl) {
        return new bootstrap.Tooltip(tooltipTriggerEl);
      });
    }, 500);
  }
  removeToolTips() {
    var tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    );

    tooltipTriggerList.forEach(function (tooltipTriggerEl) {
      const tooltipInstance = bootstrap.Tooltip.getInstance(tooltipTriggerEl);
      if (tooltipInstance) {
        tooltipInstance.dispose();
      }
    });
    const tooltipElements = document.querySelectorAll('.tooltip');
    tooltipElements.forEach(function (tooltipEl) {
      tooltipEl.remove();
    });
  }
  convertVersionToInteger(version: string) {
    return Number(version?.split('.')?.join(''));
  }
  async getMinVersionCheckDevice() {
    return new Promise(async (resolve) => {
      let deviceInfo: any = {};
      try {
        deviceInfo = await this.deviceInfoService.setAndGetDeviceInfo();
        deviceInfo = JSON.parse(deviceInfo);
      } catch (error) {}

      const currentBrowserVersion = this.convertVersionToInteger(
        deviceInfo?.browser_version,
      );

      if (deviceInfo?.browser && currentBrowserVersion) {
        this.getDeviceMinVersionCheck({ browser: deviceInfo?.browser }).then(
          (response: any) => {
            const minSupportVersionDevice = response?.data?.find(
              (device: any) =>
                device?.browser?.toLowerCase() ===
                  deviceInfo?.browser?.toLowerCase() &&
                currentBrowserVersion <
                  Number(
                    String(
                      this.convertVersionToInteger(device?.min_version),
                    )?.padEnd(String(currentBrowserVersion)?.length, '0'),
                  ) &&
                (!device?.device ||
                  device?.device?.toLowerCase() ===
                    deviceInfo?.device?.toLowerCase()) &&
                (!device?.device_type ||
                  device?.device_type?.toLowerCase() ===
                    deviceInfo?.deviceType?.toLowerCase()) &&
                (!device?.device_os ||
                  device?.device_os?.toLowerCase() ===
                    deviceInfo?.os?.toLowerCase()) &&
                (!device?.os_version ||
                  device?.os_version?.toLowerCase() ===
                    deviceInfo?.os_version?.toLowerCase()),
            );
            resolve(minSupportVersionDevice);
          },
        );
      } else {
        resolve({});
      }
    });
  }

  onAccountChange(res: any) {
    const gtAppTheme: any = this.getGTAppTheme();
    const uniqueDeviceID = localStorage.getItem(btoa('uniqueDeviceID'));
    const userLinkedAccountTokens: any =
      JSON.parse(
        localStorage.getItem(btoa('userLinkedAccountTokens')) || '[]',
      ) || [];
    localStorage.clear();

    this.setUserData(res);
    this.setGTAppTheme(gtAppTheme);
    localStorage.setItem('loginSuccess', 'true');

    localStorage.setItem(
      btoa('userLinkedAccountTokens'),
      JSON.stringify(userLinkedAccountTokens),
    );
    if (uniqueDeviceID)
      localStorage.setItem(btoa('uniqueDeviceID'), uniqueDeviceID);
  }
}
